import {
  CHAIN_MULTIPLIER,
  CHAIN_WINDOW_TICKS,
  CLEAR_RETURN_GRACE_TICKS,
  CRITICAL_WINDOW_TICKS,
  DWELL_TICKS,
  DWELL_WARNING_TICKS,
  FACILITY_CAPACITY,
  FEED_GRACE_TICKS,
  GAME_VERSION,
  LOAD_WINDOW_TICKS,
  OVERFLOW_SEVERE,
  OVERLOAD_TICKS,
  PORTAL_REARM_TICKS,
  RECEIVER_MULTIPLIER,
  SHIFT_TICKS,
  TRASH,
  bulkOf,
  gradeMultiplier,
  type ArenaAnchor,
  type GameSeed,
  type LedgerEvent,
  type LoadRecord,
  type ReceiverTier,
  type Route,
  type RunBundle,
  type RunEndReason,
  type RunMetrics,
  type SimulationAction,
  type SpawnSpec,
  type TicketSpec,
  type TraceFrame,
  type TrashKind,
} from "./types.js";
import {
  BAFFLE_TRANSIT_TICKS,
  baffleGateAt,
  baffleParamsFor,
  baffleYawAt as baffleYaw,
  type BaffleParams,
} from "./baffle.js";

interface PendingLoad {
  record: LoadRecord;
  ticketEligible: boolean;
  chamberEntered: boolean;
  baffleDeflected: boolean;
  laneJammedAtRelease: boolean;
  /**
   * Body ids of this load's pieces that PHYSICALLY reached the chamber before
   * resolution (LOCKED-DESIGN 18.14 / F8 direction C). Deduped by id so a
   * repeated `arrive` cannot inflate the count. Pure pending-load state: never
   * serialized, rebuilt from the recorded action stream on replay.
   */
  arrived: Set<number>;
  /**
   * Whether an arrival has ALREADY raised this load's `delivered`, so
   * `metrics.loadsUpgraded` counts the load once no matter how many of its
   * pieces land (the per-piece count is `metrics.upgradesApplied`).
   */
  upgraded: boolean;
}

/** Fixed lane iteration order so per-lane bookkeeping stays deterministic. */
const LANES: readonly ReceiverTier[] = ["safe", "technical", "hazard"];

interface TicketProgress {
  ticket: TicketSpec;
  counts: Partial<Record<TrashKind, number>>;
  completed: boolean;
  expired: boolean;
}

/** One recipe line for the HUD dispatch panel: pieces required vs accumulated. */
export interface TicketRecipeEntry {
  kind: Exclude<TrashKind, "husk">;
  required: number;
  have: number;
}

/** Live view of the currently active dispatch ticket (LOCKED-DESIGN 9, 18.10). */
export interface ActiveTicketView {
  id: string;
  entries: TicketRecipeEntry[];
  receiver: ReceiverTier | null;
  secondsLeft: number;
  windowSeconds: number;
}

/** Locked next-ticket preview (Section 9 "the next ticket previewed"). */
export interface TicketPreview {
  entries: TicketRecipeEntry[];
  receiver: ReceiverTier | null;
  startsIn: number;
}

/**
 * Section 8.3 freshness of a candidate route (LOCKED-DESIGN 18.10 fourth
 * dogfood): what the NEXT clean load on that exact anchor/receiver pair would
 * do to the Grade. `state` names the visible tag, `gradeDelta` the nominal
 * Section 8.3 tuning value the sim would apply (+6 varied / +3 first repeat /
 * −2 stale, before the 0..100 clamp).
 */
export interface RouteFreshness {
  state: "fresh" | "repeat" | "stale";
  gradeDelta: number;
}

/**
 * Section 8.3 Grade attribution for one RESOLVED load (LOCKED-DESIGN 18.10
 * fourth dogfood): the cause + nominal Grade delta the sim applied when it
 * scored that load. `delta` is the tuning value before the 0..100 clamp.
 */
export interface GradeAttribution {
  cause: "varied" | "repeat" | "stale" | "miss" | "partial";
  delta: number;
}

/**
 * One applied Grade movement (LOCKED-DESIGN 18.10 fourth dogfood: "Grade-meter
 * deltas attributed to their cause" — for EVERY mover, with the delta the
 * meter actually showed). `nominal` is the rule's tuning value; `applied` is
 * the movement after the sim's own clamps (0..100, the miss floor, the
 * purge/furnace band drop), so a chip built from it can never contradict the
 * visible fill.
 */
export interface GradeMove {
  cause: GradeAttribution["cause"] | "dispatch" | "detonation" | "purge" | "furnace";
  nominal: number;
  applied: number;
}

/**
 * Materialized shift-change telegraph (LOCKED-DESIGN 10, 18.10): the current
 * phase, the next scheduled change with an exact countdown, and — in the
 * five-second warning window — what materially changes (receivers opening or
 * closing, conveyor reversal, lane closures). Pure read of the seed schedule.
 */
export interface ShiftChangeView {
  phaseIndex: number;
  label: string;
  nextLabel: string;
  secondsToNext: number;
  isShiftEnd: boolean;
  warning: boolean;
  placementSeconds: number;
  changes: string[];
}

const emptyMetrics = (): RunMetrics => ({
  loads: { clean: 0, partial: 0, missed: 0 },
  routeUsage: {}, ticketCompleted: 0, ticketExpired: 0, overflowPeak: 0,
  redDetonations: 0, jams: 0, emergencyPurges: 0, portalPlacements: 0, jamBulkFed: 0,
  loadsUpgraded: 0, upgradesApplied: 0,
});

export interface SimulationOptions {
  runId?: string;
  practicePhase?: number;
  recordTrace?: boolean;
  /**
   * Simple-only accessibility valve (LOCKED-DESIGN 18.14 / playtest F4): when
   * true, a partial/missed load NEVER jams its lane. The kernel itself refuses
   * the flag on ranked seeds (see the constructor), so replay and authoritative
   * validation can never disagree and no separate anti-cheat check can drift.
   */
  jamsDisabled?: boolean;
}

export class Simulation {
  readonly runId: string;
  readonly ledger: LedgerEvent[] = [];
  readonly trace: TraceFrame[] = [];
  readonly loads: LoadRecord[] = [];
  readonly metrics = emptyMetrics();
  readonly failures: Array<{ tick: number; cause: string; detail?: string }> = [];

  tick = 0;
  score = 0;
  grade = 25;
  chainTier = 0;
  chainExpiresAt = 0;
  overflow = 0;
  criticalExpiresAt: number | null = null;
  ended = false;
  endReason: RunEndReason = "complete";
  activeRoute: Route = { anchor: "floor", receiver: "safe" };
  /**
   * Per-lane jammed reject BULK (LOCKED-DESIGN 18.12 m1.3.0 feed-the-maw
   * redesign, superseding the old clear-tick model). A lane is jammed while its
   * bulk is > 0; there is NO auto-clear and NO timer — the pile only drains via
   * Engine-issued `feed` actions as the player hand-feeds rejects to the maw. A
   * jammed hazard lane never rejects or delays loads routed to safe/technical,
   * and vice versa. Portal reconfiguration stays interlocked while ANY lane is
   * jammed OR the player occupies the chamber (Section 3.3, now implemented).
   */
  readonly laneJamBulk: Record<ReceiverTier, number> = { safe: 0, technical: 0, hazard: 0 };
  /**
   * Chamber occupancy (LOCKED-DESIGN 18.12 / 3.3). The region the player
   * occupies, derived from honoured portal crossings whose `side` the Engine
   * stamps from the same chamberThresholdZ predicate the camera uses, plus the
   * furnace-contact and fall-respawn exits. INVARIANT: the Engine's physical
   * traversal gate (playerPortalArmed + rearm + clearance) is strictly tighter
   * than the sim's re-arm rule, so any player crossing the Engine sends is
   * always honoured here — dropped (re-arm) crossings never transition.
   */
  playerSide: "arena" | "chamber" = "arena";
  /** Absolute tick the chamber cooks the player, or null while arena-side (LOCKED-DESIGN 18.12). */
  dwellExpiresAt: number | null = null;
  /**
   * Absolute tick a neglected any-lane jam overloads the furnace, or null while
   * no lane is jammed (LOCKED-DESIGN 18.12). Armed when any-jam transitions
   * false→true; NOT reset when a second lane jams (one shared continuous clock);
   * disarmed when every lane clears. Overload only ends the run past this tick
   * if no drained feed landed within FEED_GRACE_TICKS (feed-grace fairness).
   */
  overloadAt: number | null = null;
  /** Tick of the last feed that actually DRAINED bulk (feed-grace anchor). */
  lastLaneFeedAt = Number.NEGATIVE_INFINITY;
  purgeLockedUntil = 0;
  portalTraversals = 0;
  /**
   * Per-subject portal re-arm deadlines (LOCKED-DESIGN 3.4/18.10), keyed by
   * subject identity — "player" for the singular player, "trash:<id>" for a
   * specific trash body. A crossing whose subject deadline is still in the
   * future is dropped by the shared re-arm rule. Pure deterministic state:
   * rebuilt from the tick-stamped trace on replay, never serialized into the
   * bundle. Keyed lookups only — never iterated for logic — so it stays
   * order-independent.
   */
  private readonly portalRearmUntil = new Map<string, number>();
  bestCombo = 1;
  lastReleasePrediction: { loadId: string; delivered: number } | null = null;
  /**
   * Simple-only "jams never occur" run option (LOCKED-DESIGN 18.14 / F4).
   * Locked semantics: jamsDisabled means JAMS never occur — it does NOT mean
   * you never miss. Chain/Grade penalties for missed/partial loads still apply
   * in full. With no lane ever jamming, `anyLaneJammed()` is permanently false,
   * so the Section 3.3 placement interlock, the overload timer and the
   * jam-overload run end never engage; chamber dwell/"cooked" is unchanged.
   * Readable so the presentation layer can render the toggle state without a
   * parallel copy of the ranked-seed refusal rule.
   */
  readonly jamsDisabled: boolean;

  private seq = 0;
  private nextSpawn = 0;
  private nextLoad = 1;
  private lastCleanRoute = "";
  private cleanRouteRepeats = 0;
  private pendingLoads: PendingLoad[] = [];
  private pendingPlacements: Array<{ endpoint: "blue" | "orange"; target: ArenaAnchor | ReceiverTier; readyAt: number }> = [];
  private deferredRed: GameSeed["spawns"][number][] = [];
  private liveRed = 0;
  private readonly baffleParams: BaffleParams;
  private readonly tickets: TicketProgress[];
  private readonly practiceStartTick: number;
  private readonly recordTrace: boolean;

  constructor(readonly seed: GameSeed, options: SimulationOptions = {}) {
    this.runId = options.runId ?? makeRunId(seed.id);
    this.baffleParams = baffleParamsFor(seed.rngSeed);
    const phase = clamp(Math.floor(options.practicePhase ?? 1), 1, 6);
    this.practiceStartTick = (phase - 1) * 1800;
    this.tick = this.practiceStartTick - 300;
    this.nextSpawn = seed.spawns.findIndex((spawn) => spawn.tick >= this.tick);
    if (this.nextSpawn < 0) this.nextSpawn = seed.spawns.length;
    this.recordTrace = options.recordTrace ?? true;
    // The KERNEL refuses the F4 toggle on ranked seeds (LOCKED-DESIGN 18.14):
    // an official bundle carrying jamsDisabled simply replays with jams intact,
    // so replay/authoritative can never disagree with the recorder and there is
    // no separate anti-cheat predicate to drift out of sync.
    this.jamsDisabled = (options.jamsDisabled ?? false) && seed.ruleset === "simple";
    this.tickets = seed.tickets.map((ticket) => ({ ticket, counts: {}, completed: false, expired: false }));
    // Ride run-started only when TRUE so non-toggle bundles stay minimal-diff.
    this.emit("run-started", { seedId: seed.id, seedVersion: seed.version, practicePhase: phase, ...(this.jamsDisabled ? { jamsDisabled: true } : {}) });
  }

  get phase(): "playing" | "ended" { return this.ended ? "ended" : "playing"; }
  get timeLeft(): number { return Math.max(0, (SHIFT_TICKS - Math.max(this.practiceStartTick, this.tick)) / 60); }
  get elapsed(): number { return Math.max(0, this.tick - this.practiceStartTick) / 60; }
  get combo(): number { return this.chainTier + 1; }
  get incinerated(): number { return this.loads.reduce((sum, load) => sum + load.delivered, 0); }
  get phaseIndex(): number { return Math.min(6, Math.floor(Math.max(this.practiceStartTick, this.tick) / 1800) + 1); }
  get placementCountdown(): number { return Math.max(0, (this.practiceStartTick - this.tick) / 60); }
  /**
   * True while a blue-anchor placement is armed but not yet swapped (read-only
   * view of the one-slot placement queue). The Simple v2 auto-move rule
   * (Engine) checks this so its declarative retry never double-queues a move —
   * a jam spanning a phase boundary just swaps late, and the rule re-evaluates
   * every tick against this without stacking requests. Pure read; no mutation.
   */
  get bluePlacementPending(): boolean { return this.pendingPlacements.some((placement) => placement.endpoint === "blue"); }
  // The phaseLabel / nextChangeLabel / activeTicketLabel string getters were
  // removed in Phase 4: the HUD migrated to the structured shiftChange() /
  // activeTicket() views, leaving them unreferenced (LOCKED-DESIGN 18.10
  // residual cleanup). No kernel behavior touched — pure read getters.
  /**
   * Structured view of the active dispatch ticket for the HUD panel
   * (LOCKED-DESIGN 9 / 18.10). Read-only: reports live per-kind accumulation
   * against the recipe, the required receiver clause, and the remaining window.
   * Emits no ledger event and never mutates ticket bookkeeping.
   */
  activeTicket(): ActiveTicketView | null {
    const progress = this.tickets.find(({ ticket, completed, expired }) => !completed && !expired && this.tick >= ticket.startsAt && this.tick < ticket.expiresAt);
    if (!progress) return null;
    return {
      id: progress.ticket.id,
      entries: recipeEntries(progress.ticket, progress.counts),
      receiver: progress.ticket.receiver ?? null,
      secondsLeft: Math.max(0, (progress.ticket.expiresAt - this.tick) / 60),
      windowSeconds: Math.max(0, (progress.ticket.expiresAt - progress.ticket.startsAt) / 60),
    };
  }
  /**
   * Locked next-ticket preview (Section 9): the earliest ticket not yet active
   * and still winnable, with its recipe, route clause, and time until it opens.
   * Pure read of the seed's deterministic ticket schedule.
   */
  nextTicket(): TicketPreview | null {
    let best: TicketProgress | null = null;
    for (const progress of this.tickets) {
      if (progress.completed || progress.expired || progress.ticket.startsAt <= this.tick) continue;
      if (!best || progress.ticket.startsAt < best.ticket.startsAt) best = progress;
    }
    if (!best) return null;
    return {
      entries: recipeEntries(best.ticket, {}),
      receiver: best.ticket.receiver ?? null,
      startsIn: Math.max(0, (best.ticket.startsAt - this.tick) / 60),
    };
  }
  /**
   * Shift-change telegraph (LOCKED-DESIGN 10 / 18.10): the current phase, the
   * next scheduled boundary with an exact countdown, and the material changes
   * that boundary brings once inside the locked five-second warning window.
   * Derived entirely from the seed's phase schedule — no ledger, no mutation.
   */
  shiftChange(): ShiftChangeView {
    const current = this.phaseSpec();
    const placementSeconds = this.placementCountdown;
    const nextTick = Math.min(SHIFT_TICKS, (Math.floor(this.tick / 1800) + 1) * 1800);
    const secondsToNext = Math.max(0, (nextTick - this.tick) / 60);
    const isShiftEnd = nextTick >= SHIFT_TICKS;
    const next = isShiftEnd ? null : this.seed.phases[Math.min(5, Math.floor(nextTick / 1800))]!;
    const changes: string[] = [];
    if (next && placementSeconds <= 0) {
      for (const lane of LANES) {
        if (next.activeReceivers.includes(lane) && !current.activeReceivers.includes(lane)) changes.push(`${lane} receiver opens`);
        if (!next.activeReceivers.includes(lane) && current.activeReceivers.includes(lane)) changes.push(`${lane} receiver closes`);
      }
      if (next.conveyor !== current.conveyor) changes.push(`belt ${next.conveyor === "reverse" ? "reverses" : "runs forward"}`);
      if (next.closedLane && next.closedLane !== current.closedLane) changes.push(`${next.closedLane} lane closes`);
      if (current.closedLane && current.closedLane !== next.closedLane) changes.push(`${current.closedLane} lane reopens`);
      for (const anchor of next.activeAnchors) if (!current.activeAnchors.includes(anchor)) changes.push(`${anchor} anchor opens`);
      for (const anchor of current.activeAnchors) if (!next.activeAnchors.includes(anchor)) changes.push(`${anchor} anchor closes`);
    }
    return {
      phaseIndex: this.phaseIndex,
      label: current.label,
      nextLabel: isShiftEnd ? "SHIFT END" : next!.label,
      secondsToNext: placementSeconds > 0 ? placementSeconds : secondsToNext,
      isShiftEnd,
      warning: placementSeconds <= 0 && !isShiftEnd && secondsToNext <= 5 && secondsToNext > 0 && changes.length > 0,
      placementSeconds,
      changes,
    };
  }
  /**
   * Projected clean-load score for the held volley at the current route
   * (LOCKED-DESIGN 12.1 reticle dock). A pure evaluation of the deterministic
   * scoring model (Section 8.1) assuming a clean delivery with the live Chain
   * and Grade snapshots — it reads sim state and never advances or records it.
   * Truthfulness rider (18.10 fourth dogfood): a release RIGHT NOW into a
   * jammed lane or a blocked hazard baffle is rejected whole by release(), so
   * the projection reports 0 under exactly the same gates release() applies.
   */
  projectedScore(pieces: readonly TrashKind[], route: Route): number {
    if (pieces.length === 0) return 0;
    const phase = this.phaseSpec();
    const routeValid = phase.activeAnchors.includes(route.anchor) && phase.activeReceivers.includes(route.receiver);
    if (!routeValid) return 0;
    // mirror release(): the lane's own jam rejects the load whole …
    if (this.laneJammed(route.receiver)) return 0;
    // … and a blocked hazard throat deflects it (same lead-compensated gate)
    if (route.receiver === "hazard" && baffleGateAt(this.tick + BAFFLE_TRANSIT_TICKS, this.baffleParams).blocked) return 0;
    let subtotal = pieces.reduce((sum, kind) => sum + TRASH[kind].value, 0);
    subtotal += 100;
    subtotal += Math.min(150, Math.max(0, bulkOf(pieces) - 3) * 50);
    const receiver = RECEIVER_MULTIPLIER[route.receiver];
    const chain = CHAIN_MULTIPLIER[this.chainTier] ?? 300;
    const grade = gradeMultiplier(this.grade);
    return Math.floor(subtotal * receiver * chain * grade / 1_000_000);
  }
  /**
   * Best ALTERNATIVE route for the SAME held volley (LOCKED-DESIGN 18.10
   * fourth dogfood: "the projected-score readout … so switching routes visibly
   * pays"). Scans every active anchor/receiver pair other than the committed
   * route with the SAME truthful projection as projectedScore, breaking score
   * ties toward the freshest candidate (Section 8.3 economics). Pure read —
   * no ledger event, no mutation; the HUD may poll it every frame.
   */
  projectedAlternative(pieces: readonly TrashKind[]): { route: Route; score: number; freshness: RouteFreshness } | null {
    if (pieces.length === 0) return null;
    const phase = this.phaseSpec();
    let best: { route: Route; score: number; freshness: RouteFreshness } | null = null;
    for (const anchor of phase.activeAnchors) {
      for (const receiver of phase.activeReceivers) {
        if (anchor === this.activeRoute.anchor && receiver === this.activeRoute.receiver) continue;
        const route: Route = { anchor, receiver };
        const score = this.projectedScore(pieces, route);
        const freshness = this.routeFreshness(route);
        if (!best || score > best.score || (score === best.score && freshness.gradeDelta > best.freshness.gradeDelta)) {
          best = { route, score, freshness };
        }
      }
    }
    return best;
  }
  /**
   * Read-only bulk currently in portal transit / furnace processing (the
   * Section 7 "portal transit" overflow bucket, LOCKED-DESIGN 18.10 third
   * dogfood): the pieces of released loads still awaiting resolution. Pure
   * reduce over pendingLoads — no ledger event, no mutation — so the HUD
   * overflow breakdown may poll it every frame without perturbing the
   * deterministic stream.
   */
  get transitBulk(): number { return this.pendingLoads.reduce((sum, pending) => sum + bulkOf(pending.record.pieces), 0); }
  /** Jammed reject bulk on a lane (0 when clear). LOCKED-DESIGN 18.12. */
  jamBulk(lane: ReceiverTier): number { return this.laneJamBulk[lane]; }
  laneJammed(lane: ReceiverTier): boolean { return this.laneJamBulk[lane] > 0; }
  private anyLaneJammed(): boolean { return LANES.some((lane) => this.laneJamBulk[lane] > 0); }
  /** Seed-parameterized dwell/overload windows (kernel-read rules, LOCKED-DESIGN 18.12). */
  dwellTicksFor(seed: GameSeed): number { return seed.rules?.dwellTicks ?? DWELL_TICKS; }
  overloadTicksFor(seed: GameSeed): number { return seed.rules?.overloadTicks ?? OVERLOAD_TICKS; }

  // --- Presentation/bot surface for the jam-recovery loop (LOCKED-DESIGN 18.12).
  // All pure reads: no ledger event, no mutation; the HUD/bot may poll each tick.
  get playerInChamber(): boolean { return this.playerSide === "chamber"; }
  get dwellTicksLeft(): number { return this.playerSide === "chamber" && this.dwellExpiresAt !== null ? Math.max(0, this.dwellExpiresAt - this.tick) : 0; }
  get dwellWarning(): boolean { return this.playerSide === "chamber" && this.dwellExpiresAt !== null && this.dwellTicksLeft <= DWELL_WARNING_TICKS; }
  get overloadTicksLeft(): number | null { return this.overloadAt === null ? null : Math.max(0, this.overloadAt - this.tick); }
  /** Past the overload deadline but held alive by feed-grace (meter pins at zero, LOCKED-DESIGN 18.12). */
  get overloadHeld(): boolean { return this.overloadAt !== null && this.anyLaneJammed() && this.tick >= this.overloadAt; }
  /**
   * Hazard-baffle timing feedback (LOCKED-DESIGN 3.2 "explicit timing
   * feedback"). Read-only presentation surface consumed by the HUD hazard-gate
   * readout (Hud.ts); Phase 2+ may restyle it freely.
   * Gate getters are lead-compensated by BAFFLE_TRANSIT_TICKS — they report
   * the state a nominal hazard transit AFTER the current tick, exactly the
   * gate a release() issued this tick will face, so a HUD showing OPEN means
   * "a load released right now passes the baffle". Window transitions emit no
   * ledger events: the schedule is a pure function of tick + seed, so replay
   * derives it for free and the ledger stays lean (see baffle.ts).
   */
  get baffleBlocked(): boolean { return this.baffleGate().blocked; }
  get baffleCycleProgress(): number { return this.baffleGate().cycleProgress; }
  get baffleSecondsToNextOpen(): number { return this.baffleGate().ticksToNextOpen / 60; }
  get baffleSecondsToNextClose(): number { return this.baffleGate().ticksToNextClose / 60; }
  /** Physical bar yaw for the renderer and kinematic collider; accepts fractional ticks for interpolation. */
  baffleYawAt(tickLike: number): number { return baffleYaw(tickLike, this.baffleParams); }
  private baffleGate() { return baffleGateAt(this.tick + BAFFLE_TRANSIT_TICKS, this.baffleParams); }

  /**
   * Presentation-only (LOCKED-DESIGN 8.3): true while the ACTIVE route's next
   * clean load would be visibly `STALE` (third-or-later consecutive clean on
   * the same anchor/receiver pair). Read-only — no ledger event; replay
   * derives staleness from load outcomes for free.
   */
  get routeStale(): boolean {
    return this.routeFreshness(this.activeRoute).state === "stale";
  }

  /**
   * Section 8.3 freshness of a candidate route at decision time (LOCKED-DESIGN
   * 18.10 fourth dogfood): mirrors resolveLoad's clean-load Grade rule exactly,
   * INCLUDING the loads still in flight — a load's outcome and route are fixed
   * at release(), so the pending resolutions are already decided and fold into
   * the consecutive-clean counters here in resolution order. A release made
   * right now is therefore previewed against the counters scoring will
   * actually hold when it resolves, even under pipelined play (a second
   * same-route release before the first resolves reads REPEAT, not FRESH; a
   * pending clean on another route restores this pair to FRESH). Read-only:
   * no ledger event, no mutation.
   */
  routeFreshness(route: Route = this.activeRoute): RouteFreshness {
    let lastClean = this.lastCleanRoute;
    let repeats = this.cleanRouteRepeats;
    // pendingLoads is push-ordered and LOAD_WINDOW_TICKS is constant, so array
    // order IS resolution order — the same fold resolveLoad will perform.
    for (const { record } of this.pendingLoads) {
      if (record.outcome !== "clean") continue;
      const key = `${record.route.anchor}/${record.route.receiver}`;
      repeats = key === lastClean ? repeats + 1 : 1;
      lastClean = key;
    }
    const next = `${route.anchor}/${route.receiver}` === lastClean ? repeats + 1 : 1;
    return next === 1
      ? { state: "fresh", gradeDelta: 6 }
      : next === 2
        ? { state: "repeat", gradeDelta: 3 }
        : { state: "stale", gradeDelta: -2 };
  }

  /**
   * Grade attribution for a RESOLVED load (LOCKED-DESIGN 18.10 fourth dogfood:
   * "Grade-meter deltas attributed to their cause"). Pure fold over the
   * recorded load outcomes replaying the exact resolveLoad bookkeeping
   * (consecutive-clean route repeats), so the presented cause can never drift
   * from the rule the sim applied. `delta` is the nominal Section 8.3 tuning
   * value (before the 0..100 clamp). Read-only; O(loads) per call.
   */
  gradeAttribution(loadId: string): GradeAttribution | null {
    let lastClean = "";
    let repeats = 0;
    for (const load of this.loads) {
      let result: GradeAttribution;
      if (load.outcome === "clean") {
        const key = `${load.route.anchor}/${load.route.receiver}`;
        repeats = key === lastClean ? repeats + 1 : 1;
        lastClean = key;
        result = repeats === 1
          ? { cause: "varied", delta: 6 }
          : repeats === 2
            ? { cause: "repeat", delta: 3 }
            : { cause: "stale", delta: -2 };
      } else if (load.outcome === "missed") {
        result = { cause: "miss", delta: -10 };
      } else {
        result = { cause: "partial", delta: 0 };
      }
      if (load.id === loadId) return result;
    }
    return null;
  }

  /**
   * Applied Grade movement for one ledger event (LOCKED-DESIGN 8.3 / 18.10
   * fourth dogfood: the meter chip must match the meter). Pure fold over the
   * ledger replaying EVERY Grade mover — load outcomes with the exact
   * resolveLoad consecutive-clean bookkeeping, dispatch completions (+8,
   * applied AFTER the same resolve's load delta, exactly as resolveLoad
   * orders them), red detonations, and the purge/furnace band drops — while
   * tracking the clamped Grade value itself. Each load-resolved event carries
   * the sim's authoritative post-resolve grade, which the fold re-anchors on,
   * so the reconstruction cannot drift from what the meter showed. Read-only:
   * no ledger event, no mutation; O(ledger) per call, presentation-rate only.
   */
  gradeMoveAt(seq: number): GradeMove | null {
    const loadsById = new Map(this.loads.map((load) => [load.id, load]));
    const moves = new Map<number, GradeMove>();
    let grade = 25; // constructor initial value — never seed-dependent
    let lastClean = "";
    let repeats = 0;
    let ticketEvent: LedgerEvent | null = null;
    const lowerBand = (value: number): number => (value >= 85 ? 84 : value >= 65 ? 64 : value >= 40 ? 39 : 0);
    const applyClamped = (delta: number): number => {
      const next = clamp(grade + delta, 0, 100);
      const applied = next - grade;
      grade = next;
      return applied;
    };
    for (const event of this.ledger) {
      switch (event.type) {
        case "ticket-completed":
          // the +8 lands inside the SAME resolve, after the load's own delta
          // (applyTicketProgress runs before the load-resolved emit) — stage
          // it until that load-resolved event is folded
          ticketEvent = event;
          break;
        case "load-resolved": {
          const load = event.loadId ? loadsById.get(event.loadId) : undefined;
          if (!load) break;
          if (load.outcome === "clean") {
            const key = `${load.route.anchor}/${load.route.receiver}`;
            repeats = key === lastClean ? repeats + 1 : 1;
            lastClean = key;
            const nominal = repeats === 1 ? 6 : repeats === 2 ? 3 : -2;
            const cause = repeats === 1 ? ("varied" as const) : repeats === 2 ? ("repeat" as const) : ("stale" as const);
            moves.set(event.seq, { cause, nominal, applied: applyClamped(nominal) });
          } else if (load.outcome === "missed") {
            moves.set(event.seq, { cause: "miss", nominal: -10, applied: applyClamped(-10) });
          } else {
            moves.set(event.seq, { cause: "partial", nominal: 0, applied: 0 });
          }
          if (ticketEvent && ticketEvent.loadId === event.loadId) {
            moves.set(ticketEvent.seq, { cause: "dispatch", nominal: 8, applied: applyClamped(8) });
          }
          ticketEvent = null;
          grade = Number(event.data.grade ?? grade); // authoritative re-anchor
          break;
        }
        case "red-detonated":
          moves.set(event.seq, { cause: "detonation", nominal: -10, applied: applyClamped(-10) });
          break;
        case "emergency-purge": {
          const next = lowerBand(grade);
          moves.set(event.seq, { cause: "purge", nominal: next - grade, applied: next - grade });
          grade = next;
          break;
        }
        case "furnace-contact": {
          const next = lowerBand(grade);
          moves.set(event.seq, { cause: "furnace", nominal: next - grade, applied: next - grade });
          grade = next;
          break;
        }
      }
      const hit = moves.get(seq);
      if (hit) return hit;
    }
    return null;
  }

  /**
   * Presentation-only read-ahead of the seed's deterministic spawn schedule
   * (LOCKED-DESIGN 18.4 telegraphs): the spawns due within `withinTicks` of
   * the current tick. Pure read — never advances spawn bookkeeping, never
   * emits ledger events. Returns nothing during critical overflow (chutes are
   * shuttered; the sim skips those spawns) and says nothing about deferred
   * red orbs, which is exactly the fidelity a telegraph needs.
   */
  upcomingSpawns(withinTicks: number): SpawnSpec[] {
    if (this.criticalExpiresAt !== null || this.ended) return [];
    const horizon = this.tick + withinTicks;
    const out: SpawnSpec[] = [];
    for (let index = this.nextSpawn; index < this.seed.spawns.length; index++) {
      const spawn = this.seed.spawns[index]!;
      if (spawn.tick > horizon) break;
      if (spawn.tick >= this.tick) out.push(spawn);
    }
    return out;
  }

  rampT(): number { return Math.min(1, Math.max(0, this.tick) / SHIFT_TICKS); }
  comboFrac(): number { return this.chainTier > 0 ? Math.max(0, (this.chainExpiresAt - this.tick) / CHAIN_WINDOW_TICKS) : 0; }
  multiplier(): number { return CHAIN_MULTIPLIER[this.chainTier] / 100; }
  onTraversal(): void { this.portalTraversals++; this.emit("portal-traversal", { count: this.portalTraversals }); }

  private portalSubjectKey(subject: "player" | "trash", id?: number): string {
    return subject === "player" ? "player" : `trash:${id ?? 0}`;
  }

  /**
   * Apply a portal traversal under the shared re-arm rule (LOCKED-DESIGN 3.4 /
   * 18.10). The crossing is honoured only when the subject is not still cooling
   * down from a prior crossing; otherwise it is dropped — no counter bump, no
   * ledger event. Like the baffle schedule this stays a pure function of the
   * recorded traversal ticks, so replay reconstructs the same drops for free
   * and the ledger stays lean. A honoured crossing arms the subject's cooldown
   * for PORTAL_REARM_TICKS. The rule blocks only a SECOND crossing by the SAME
   * subject inside the window, so a normal one-way delivery — every piece of a
   * volley is a distinct body — is never impeded.
   */
  private traversePortal(subject: "player" | "trash", id?: number, side?: "arena" | "chamber"): void {
    const key = this.portalSubjectKey(subject, id);
    // Unset default is -Infinity, NOT 0: pre-roll ticks are NEGATIVE, so a 0
    // default would spuriously read as "still re-arming" and DROP the very first
    // crossing during the placement countdown — silently defeating the pre-roll
    // occupancy honesty the allowlist grants (LOCKED-DESIGN 18.12). A never-crossed
    // subject must always be free to cross. (Positive-tick fixtures are unchanged:
    // 0 and -Infinity both compare < any positive tick, so ledger hashes hold.)
    if ((this.portalRearmUntil.get(key) ?? Number.NEGATIVE_INFINITY) > this.tick) return;
    this.portalRearmUntil.set(key, this.tick + PORTAL_REARM_TICKS);
    this.onTraversal();
    this.emit("portal-subject", { subject });
    // Chamber occupancy (LOCKED-DESIGN 18.12 / 3.3): only HONOURED player
    // crossings whose `side` the Engine stamped move the occupancy state.
    // Dropped (re-arm) crossings never reach here, which is safe — the Engine's
    // physical gate is strictly tighter than the sim's re-arm rule, so every
    // Engine-sent player crossing is honoured. Absolute, so a missed event
    // self-heals on the next crossing.
    if (subject === "player" && side && side !== this.playerSide) {
      if (side === "chamber") {
        this.playerSide = "chamber";
        this.dwellExpiresAt = this.tick + this.dwellTicksFor(this.seed);
        this.emit("chamber-entered", { dwellExpiresAt: this.dwellExpiresAt, jammed: this.anyLaneJammed() });
      } else {
        const dwellRemainingTicks = this.dwellExpiresAt !== null ? Math.max(0, this.dwellExpiresAt - this.tick) : 0;
        this.playerSide = "arena";
        this.dwellExpiresAt = null;
        this.emit("chamber-exited", { cause: "traversal", dwellRemainingTicks });
      }
    }
  }

  /**
   * Read-only remaining re-arm ticks for a subject (LOCKED-DESIGN 18.10
   * presentation hook). >0 means the subject just crossed and any further
   * crossing is blocked for this many fixed ticks — the HUD/FX read this to
   * flash a brief "portal cooling" cue on the player. Pure: never mutates
   * cooldown state, so hammering it every frame cannot perturb the stream.
   */
  portalRearmTicksLeft(subject: "player" | "trash" = "player", id?: number): number {
    return Math.max(0, (this.portalRearmUntil.get(this.portalSubjectKey(subject, id)) ?? 0) - this.tick);
  }

  step(frame: Omit<TraceFrame, "tick"> | TraceFrame): void {
    if (this.ended) return;
    this.lastReleasePrediction = null;
    const actions = frame.actions ?? [];
    const normalized: TraceFrame = {
      tick: this.tick,
      moveX: quantize(frame.moveX), moveZ: quantize(frame.moveZ),
      aimX: quantize(frame.aimX), aimZ: quantize(frame.aimZ),
      buttons: frame.buttons | 0,
      ...(actions.length ? { actions: this.recordTrace ? structuredClone(actions) : actions } : {}),
    };
    if (this.recordTrace) this.trace.push(normalized);
    for (const action of normalized.actions ?? []) {
      if (action.type === "abandon" || (this.tick < SHIFT_TICKS && (this.tick >= this.practiceStartTick || action.type === "place" || action.type === "portal-traversal"))) this.apply(action);
    }
    if (this.tick < this.practiceStartTick) {
      this.processPlacements();
      this.tick++;
      return;
    }
    if (this.tick < SHIFT_TICKS) this.processSpawns();
    this.processLoads();
    this.processPlacements();
    this.processTickets();
    this.processTimers();
    if (!this.ended) {
      this.tick++;
      if (this.tick >= SHIFT_TICKS && (this.pendingLoads.length === 0 || this.tick >= SHIFT_TICKS + LOAD_WINDOW_TICKS)) this.finish("complete");
    }
  }

  stepNeutral(ticks = 1): void {
    for (let i = 0; i < ticks && !this.ended; i++) {
      this.step({ moveX: 0, moveZ: 0, aimX: 0, aimZ: 0, buttons: 0 });
    }
  }

  apply(action: SimulationAction): void {
    if (this.ended) return;
    switch (action.type) {
      case "place": this.place(action.endpoint, action.target); break;
      case "release": this.release(action.pieces, action.route ?? this.activeRoute, action.delivered); break;
      case "red-detonate": this.redDetonate(action.location); break;
      case "feed": this.feed(action.bulk, action.lane, action.disposition ?? "incinerated"); break;
      case "arrive": this.arriveLoad(action.loadId, action.id); break;
      case "emergency-purge": this.emergencyPurge(action.pieces); break;
      case "furnace-contact": {
        // The Section 3.4 respawn IS the chamber exit (LOCKED-DESIGN 18.12):
        // touching the maw ejects the player, resetting the dwell timer, before
        // the existing chain/grade penalty emits.
        if (this.playerSide === "chamber") {
          const dwellRemainingTicks = this.dwellExpiresAt !== null ? Math.max(0, this.dwellExpiresAt - this.tick) : 0;
          this.playerSide = "arena";
          this.dwellExpiresAt = null;
          this.emit("chamber-exited", { cause: "furnace-contact", dwellRemainingTicks });
        }
        this.chainTier = 0; this.lowerGradeBand(); this.emit("furnace-contact", {}); break;
      }
      case "portal-traversal": this.traversePortal(action.subject, action.id, action.side); break;
      case "abandon": this.finish("abandoned"); break;
    }
  }

  private place(endpoint: "blue" | "orange", target: ArenaAnchor | ReceiverTier): void {
    const phase = this.phaseSpec();
    if (endpoint === "blue") {
      if (!phase.activeAnchors.includes(target as ArenaAnchor)) {
        this.emit("placement-failed", { endpoint, target, cause: "inactive-anchor" });
        return;
      }
    } else {
      if (!phase.activeReceivers.includes(target as ReceiverTier)) {
        this.emit("placement-failed", { endpoint, target, cause: "inactive-receiver" });
        return;
      }
    }
    if (this.pendingPlacements.some((placement) => placement.endpoint === endpoint)) {
      this.emit("placement-failed", { endpoint, target, cause: "request-already-queued" });
      return;
    }
    this.pendingPlacements.push({ endpoint, target, readyAt: this.tick + 45 });
    this.metrics.portalPlacements++;
    this.emit("portal-armed", { endpoint, target, commitmentTicks: 45 });
  }

  private processPlacements(): void {
    for (let index = this.pendingPlacements.length - 1; index >= 0; index--) {
      const placement = this.pendingPlacements[index]!;
      // Section 3.3 reconfiguration interlock (now fully implemented,
      // LOCKED-DESIGN 18.12): a placement waits while ANY lane is jammed OR the
      // player occupies the chamber. The one-slot queue means a queued swap
      // activates automatically once both clear.
      if (placement.readyAt > this.tick || this.anyLaneJammed() || this.playerSide === "chamber") continue;
      if (placement.endpoint === "blue") this.activeRoute = { ...this.activeRoute, anchor: placement.target as ArenaAnchor };
      else this.activeRoute = { ...this.activeRoute, receiver: placement.target as ReceiverTier };
      this.pendingPlacements.splice(index, 1);
      this.emit("portal-swapped", { endpoint: placement.endpoint, target: placement.target });
    }
  }

  /**
   * `deliveredOverride` is the RECORDED-ACTION trust boundary, stated in full so
   * the `arrive` action's anti-cheat bound is not mistaken for the whole
   * surface: this field is trusted wholesale (clamped only to `pieces.length`),
   * so a forged trace can declare every load clean without emitting a single
   * `arrive`. That is deliberate and pre-existing — replay parity is the
   * authority for a local milestone, and `buildParityVector`'s forced cadences
   * depend on it — and it is harmless today because the real Engine NEVER emits
   * `delivered` (see Engine's release assembly): live runs always take the
   * deterministic roll. Authoritative validation replays the same actions, so a
   * forged trace is self-consistent by construction; the eventual leaderboard
   * Worker is where a recorded `delivered` must be refused outright, alongside
   * pinning the submitted seed (see `authoritative.validateM1`).
   */
  private release(pieces: TrashKind[], route: Route, deliveredOverride?: number): void {
    if (pieces.length === 0 || bulkOf(pieces) > 6) {
      this.emit("release-failed", { cause: pieces.length === 0 ? "empty" : "capacity", bulk: bulkOf(pieces) });
      return;
    }
    const phase = this.phaseSpec();
    const routeValid = phase.activeAnchors.includes(route.anchor) && phase.activeReceivers.includes(route.receiver);
    // Only the load's own lane can reject it (Section 6: a jam disables the
    // downstream receiver lane, not the other lanes or the portal field).
    const laneJammedAtRelease = this.laneJammed(route.receiver);
    // Hazard baffle gate (LOCKED-DESIGN 3.2): a load arriving while the seeded
    // baffle crosses the throat is deflected whole — a furnace-lane reject per
    // Section 6, overriding any delivered hint; physics cannot pass a blocked
    // throat. The gate reads the arrival tick (release + nominal transit),
    // the same instant the baffle getters advertise.
    const baffleDeflected = routeValid && route.receiver === "hazard" && !laneJammedAtRelease
      && baffleGateAt(this.tick + BAFFLE_TRANSIT_TICKS, this.baffleParams).blocked;
    const delivered = clamp(laneJammedAtRelease || baffleDeflected ? 0 : (deliveredOverride ?? deterministicDelivered(pieces, route, this.tick, this.seed.rngSeed, routeValid)), 0, pieces.length);
    const id = `${this.runId}:load:${this.nextLoad++}`;
    const record: LoadRecord = {
      id, releasedAt: this.tick, resolvesAt: this.tick + LOAD_WINDOW_TICKS,
      pieces: [...pieces], route: { ...route }, delivered,
      outcome: delivered === pieces.length ? "clean" : delivered > 0 ? "partial" : "missed",
      score: 0, chainAtRelease: this.chainTier, gradeAtRelease: this.grade,
    };
    const activeTicket = this.tickets.find(({ ticket, completed }) => !completed && this.tick >= ticket.startsAt && this.tick < ticket.expiresAt);
    const ticketEligible = activeTicket ? this.ticketEligible(activeTicket.ticket, pieces, route) : false;
    this.pendingLoads.push({ record, ticketEligible, chamberEntered: routeValid, baffleDeflected, laneJammedAtRelease, arrived: new Set<number>(), upgraded: false });
    this.lastReleasePrediction = { loadId: id, delivered };
    this.emit("load-released", {
      pieces: pieces.join(","), bulk: bulkOf(pieces), anchor: route.anchor, receiver: route.receiver,
      chain: this.chainTier + 1, grade: this.grade, ticketEligible,
    }, id, activeTicket?.ticket.id);
  }

  /**
   * Physical-arrival UPGRADE (LOCKED-DESIGN 18.14, F8 direction C). One thrown
   * piece of a still-pending load physically crossed into the chamber before
   * the 3s resolution, so the deterministic dice floor is raised toward a clean
   * delivery. Upgrade-only: it can never LOWER `delivered`, so a bad throw
   * still gets the generous floor and a good throw is rewarded.
   *
   * ANTI-CHEAT BOUND: bounded by `pieces.length` (no minting), scoped to a
   * still-pending load (no resurrection), refused on whole-load rejects (jam,
   * baffle) and on an invalid route, and deduped by body id. It cannot change
   * kinds/route/receiver multiplier; Chain and Grade move only through the
   * normal `resolveLoad` outcome path. Same trust boundary the design already
   * accepts for `release.pieces` and `feed.bulk` — replay parity is the
   * authority, not physical validation.
   */
  private arriveLoad(loadId: string, pieceId: number): void {
    const pending = this.pendingLoads.find((p) => p.record.id === loadId);
    if (!pending) return;                                                // unknown / already resolved
    if (!pending.chamberEntered) return;                                 // invalid route stays 0
    if (pending.laneJammedAtRelease || pending.baffleDeflected) return;  // whole-load rejects are never upgraded
    const id = pieceId | 0;
    if (pending.arrived.has(id)) return;                                 // dedupe by body id
    pending.arrived.add(id);
    const upgraded = Math.min(pending.arrived.size, pending.record.pieces.length);
    if (upgraded <= pending.record.delivered) return;                    // upgrade-only
    pending.record.delivered = upgraded;
    pending.record.outcome = upgraded === pending.record.pieces.length ? "clean" : "partial";
    // Two counters on purpose: `upgradesApplied` is per credited PIECE (a
    // two-piece load carried missed → partial → clean adds 2), `loadsUpgraded`
    // is per rescued LOAD (that same load adds 1). The dogfood question — "did
    // aim move outcomes" — is asked of loads, so the names must not blur.
    this.metrics.upgradesApplied++;
    if (!pending.upgraded) {
      pending.upgraded = true;
      this.metrics.loadsUpgraded++;
    }
    this.emit("load-upgraded", { delivered: upgraded, total: pending.record.pieces.length, cause: "physical-arrival" }, loadId);
  }

  private processLoads(): void {
    const due = this.pendingLoads.filter(({ record }) => record.resolvesAt <= this.tick);
    this.pendingLoads = this.pendingLoads.filter(({ record }) => record.resolvesAt > this.tick);
    for (const pending of due) this.resolveLoad(pending);
  }

  private resolveLoad({ record, ticketEligible, chamberEntered, baffleDeflected, laneJammedAtRelease }: PendingLoad): void {
    const deliveredPieces = record.pieces.slice(0, record.delivered);
    const outcome = record.outcome;
    this.metrics.loads[outcome]++;
    const routeKey = `${record.route.anchor}/${record.route.receiver}`;
    this.metrics.routeUsage[routeKey] = (this.metrics.routeUsage[routeKey] ?? 0) + 1;
    let subtotal = deliveredPieces.reduce((sum, kind) => sum + TRASH[kind].value, 0);
    if (outcome === "clean") {
      subtotal += 100;
      subtotal += Math.min(150, Math.max(0, bulkOf(record.pieces) - 3) * 50);
    }
    const receiver = RECEIVER_MULTIPLIER[record.route.receiver];
    const chain = CHAIN_MULTIPLIER[record.chainAtRelease] ?? 300;
    const grade = gradeMultiplier(record.gradeAtRelease);
    record.score = Math.floor(subtotal * receiver * chain * grade / 1_000_000);
    this.score += record.score;

    const deliveredBulk = bulkOf(deliveredPieces);
    this.liveRed = Math.max(0, this.liveRed - deliveredPieces.filter((kind) => kind === "red").length);
    this.overflow = Math.max(0, this.overflow - deliveredBulk);
    if (record.delivered < record.pieces.length && chamberEntered) {
      const lane = record.route.receiver;
      const rejected = record.pieces.slice(record.delivered);
      if (this.jamsDisabled) {
        // F4 Simple-only valve (LOCKED-DESIGN 18.14): the rejects are handed
        // straight back to the arena instead of forming a jam pile — no
        // metrics.jams, no laneJamBulk, no overloadAt, so the Section 3.3
        // interlock and the jam-overload run end can never engage. The load's
        // own Chain/Grade penalty (missed/partial) still applies below,
        // unchanged: "jams never occur" is NOT "you never miss".
        this.emit("reject-returned", { receiver: lane, bulk: bulkOf(rejected) }, record.id);
      } else {
        this.metrics.jams++;
        // Section 6 / 18.12: the jam is scoped to this load's downstream lane and
        // is measured in reject BULK — there is no auto-clear. More trash into an
        // already-jammed lane simply enlarges the pile the player must hand-feed.
        const wasJammed = this.anyLaneJammed();
        this.laneJamBulk[lane] += bulkOf(rejected);
        // Overload arms only when any-jam transitions false→true — one shared
        // continuous clock; a second lane jamming does NOT reset it (18.12).
        let overloadArmedAt: number | undefined;
        if (!wasJammed) {
          this.overloadAt = this.tick + this.overloadTicksFor(this.seed);
          overloadArmedAt = this.overloadAt;
        }
        // Explicit failure cause (AGENTS.md telemetry standard) on the existing
        // event grammar. `overloadAt` rides only the event that armed the timer.
        const cause = baffleDeflected ? "baffle-deflected" : laneJammedAtRelease ? "jam-extended" : "furnace-reject";
        this.emit("receiver-jammed", {
          receiver: lane, rejectedBulk: bulkOf(rejected), totalBulk: this.laneJamBulk[lane], cause,
          ...(overloadArmedAt !== undefined ? { overloadAt: overloadArmedAt } : {}),
        }, record.id);
      }
    }

    if (outcome === "clean") {
      const sameRoute = routeKey === this.lastCleanRoute;
      this.cleanRouteRepeats = sameRoute ? this.cleanRouteRepeats + 1 : 1;
      this.lastCleanRoute = routeKey;
      this.grade = clamp(this.grade + (this.cleanRouteRepeats === 1 ? 6 : this.cleanRouteRepeats === 2 ? 3 : -2), 0, 100);
      this.chainTier = Math.min(4, this.chainTier + 1);
      this.bestCombo = Math.max(this.bestCombo, this.chainTier + 1);
    } else if (outcome === "partial") {
      // partial preserves Chain and Grade
    } else {
      this.chainTier = 0;
      this.grade = Math.max(0, this.grade - 10);
    }
    if (outcome !== "missed") this.chainExpiresAt = this.tick + CHAIN_WINDOW_TICKS;

    if (ticketEligible && record.delivered > 0) this.applyTicketProgress(deliveredPieces, record.route, record.id);
    this.loads.push(record);
    this.emit("load-resolved", { outcome, delivered: record.delivered, total: record.pieces.length, score: record.score, chain: this.chainTier + 1, grade: this.grade }, record.id);
  }

  private ticketEligible(ticket: TicketSpec, pieces: TrashKind[], route: Route): boolean {
    if (ticket.receiver && ticket.receiver !== route.receiver) return false;
    return pieces.every((kind) => kind !== "husk" && (ticket.recipe[kind] ?? 0) > 0);
  }

  private applyTicketProgress(pieces: TrashKind[], route: Route, loadId: string): void {
    const progress = this.tickets.find(({ ticket, completed }) => !completed && this.tick >= ticket.startsAt && this.tick <= ticket.expiresAt && this.ticketEligible(ticket, pieces, route));
    if (!progress) return;
    for (const kind of pieces) progress.counts[kind] = (progress.counts[kind] ?? 0) + 1;
    const complete = Object.entries(progress.ticket.recipe).every(([kind, required]) => (progress.counts[kind as TrashKind] ?? 0) >= (required ?? 0));
    if (!complete) {
      this.emit("ticket-progress", { counts: JSON.stringify(progress.counts) }, loadId, progress.ticket.id);
      return;
    }
    progress.completed = true;
    this.metrics.ticketCompleted++;
    const requiredBulk = Object.entries(progress.ticket.recipe).reduce((sum, [kind, count]) => sum + TRASH[kind as TrashKind].bulk * (count ?? 0), 0);
    const bonus = Math.floor(requiredBulk * 100 * RECEIVER_MULTIPLIER[progress.ticket.receiver ?? "safe"] / 100);
    this.score += bonus;
    this.grade = Math.min(100, this.grade + 8);
    this.emit("ticket-completed", { bonus, requiredBulk }, loadId, progress.ticket.id);
  }

  private redDetonate(location: "arena" | "chamber"): void {
    this.liveRed = Math.max(0, this.liveRed - 1);
    this.metrics.redDetonations++;
    this.chainTier = 0;
    this.grade = Math.max(0, this.grade - 10);
    // Section 5 "extends the jam" is now honored physically, not by a tick
    // extension (LOCKED-DESIGN 18.12): a chamber blast scatters the reject pile,
    // which costs feeding time under the overload clock. Chain reset + Grade −10
    // stay; the deleted +90-tick extension is gone.
    this.emit("red-detonated", { location, beamJamTicks: 45 });
  }

  /**
   * Zero-point maw feed (LOCKED-DESIGN 18.12). Issued by the Engine when a
   * reject piece physically leaves the jam (into the maw intake, back to the
   * arena, or completing the return-belt ride). Drains the named lane's pile,
   * honors Section 7 overflow accounting for incinerated bulk, and NEVER touches
   * score/Chain/Grade/tickets/loads (the chamber is recovery-only, 3.4). Feed
   * does NOT require chamber occupancy — a chamber blast can shove a reject into
   * the maw with nobody inside.
   */
  private feed(bulk: number, lane: ReceiverTier | undefined, disposition: "incinerated" | "returned"): void {
    let drained = 0;
    if (lane) {
      const before = this.laneJamBulk[lane];
      drained = Math.min(bulk, before);
      this.laneJamBulk[lane] = before - drained;
      // The anti-neglect grace valve anchors ONLY on a real maw feed (18.12): a
      // "returned" feed is bulk that physically LEFT the chamber by autonomous
      // means (belt ride, portal knock, chamber-blast scatter) — it drains the
      // pile it vacated, but the player did not feed the maw, so it must never
      // refresh the overload grace or the run-end guarantee would be defeatable
      // by activity the player never performed.
      if (drained > 0 && disposition === "incinerated") this.lastLaneFeedAt = this.tick;
    }
    // Section 7: fed bulk leaves overflow as it incinerates; a "returned" piece
    // is back in circulation and still counts, so overflow is untouched. The
    // player-facing "Recovered" tally (jamBulkFed) likewise counts only real maw
    // feeds — returned bulk was knocked out, never fed.
    if (disposition === "incinerated") {
      this.overflow = Math.max(0, this.overflow - bulk);
      this.metrics.jamBulkFed += drained;
    }
    this.emit("jam-fed", { receiver: lane ?? null, bulk, remaining: lane ? this.laneJamBulk[lane] : null, disposition, overflow: this.overflow });
    // "pile fully drained" now carries the "receiver-cleared" name (18.12).
    if (lane && drained > 0 && this.laneJamBulk[lane] === 0) this.emit("receiver-cleared", { receiver: lane, cause: "fed" });
    // Overload disarms the instant every lane clears (checked after feeds apply).
    if (this.overloadAt !== null && !this.anyLaneJammed()) this.overloadAt = null;
    // Return grace (18.12): clearing the LAST jam while still in the chamber must
    // not cook a player whose final feed landed late — guarantee a bounded window
    // to reach the return pad. Extends dwell only (Math.max), never shortens; once
    // past the window the chamber still cooks, so it is not a free loiter.
    if (drained > 0 && this.playerSide === "chamber" && this.dwellExpiresAt !== null && !this.anyLaneJammed()) {
      this.dwellExpiresAt = Math.max(this.dwellExpiresAt, this.tick + CLEAR_RETURN_GRACE_TICKS);
    }
  }

  private emergencyPurge(pieces: TrashKind[]): void {
    const bulk = bulkOf(pieces);
    if (bulk !== 6) { this.emit("purge-failed", { cause: "requires-full-load", bulk }); return; }
    if (this.purgeLockedUntil > this.tick) { this.emit("purge-failed", { cause: "locked", readyAt: this.purgeLockedUntil }); return; }
    if (bulk > 6) { this.emit("purge-failed", { cause: "capacity", bulk }); return; }
    this.overflow = Math.max(0, this.overflow - bulk);
    this.liveRed = Math.max(0, this.liveRed - pieces.filter((kind) => kind === "red").length);
    this.metrics.emergencyPurges++;
    this.purgeLockedUntil = this.tick + 1200;
    this.chainTier = 0;
    this.lowerGradeBand();
    this.emit("emergency-purge", { bulk, neutralizedRed: pieces.filter((kind) => kind === "red").length, readyAt: this.purgeLockedUntil });
  }

  private processSpawns(): void {
    while (this.nextSpawn < this.seed.spawns.length) {
      const spawn = this.seed.spawns[this.nextSpawn]!;
      if (spawn.tick > this.tick) break;
      this.nextSpawn++;
      if (spawn.tick < this.tick || this.criticalExpiresAt !== null) continue;
      if (spawn.kind === "red" && this.liveRed >= 2) { this.deferredRed.push(spawn); this.emit("red-spawn-deferred", { chute: spawn.chute, cause: "two-orb-cap" }); continue; }
      this.spawnOne(spawn);
    }
    if (this.liveRed < 2 && this.deferredRed.length > 0 && this.criticalExpiresAt === null) this.spawnOne(this.deferredRed.shift()!);
  }

  private spawnOne(spawn: GameSeed["spawns"][number]): void {
    if (spawn.kind === "red") this.liveRed++;
    this.overflow += TRASH[spawn.kind].bulk;
    this.metrics.overflowPeak = Math.max(this.metrics.overflowPeak, this.overflow);
    this.emit("trash-spawned", { kind: spawn.kind, chute: spawn.chute, bulk: TRASH[spawn.kind].bulk, overflow: this.overflow });
    if (this.overflow >= FACILITY_CAPACITY && this.criticalExpiresAt === null) {
      this.criticalExpiresAt = this.tick + CRITICAL_WINDOW_TICKS;
      this.emit("critical-overflow", { expiresAt: this.criticalExpiresAt });
    }
  }

  private processTickets(): void {
    for (const progress of this.tickets) {
      if (!progress.completed && !progress.expired && this.tick >= progress.ticket.expiresAt) {
        progress.expired = true;
        this.metrics.ticketExpired++;
        this.emit("ticket-expired", {}, undefined, progress.ticket.id);
      }
    }
  }

  private processTimers(): void {
    if (this.tick > 0 && this.tick % 1800 === 0 && this.tick < SHIFT_TICKS) {
      const phase = this.phaseSpec();
      const fallbackBlue = !phase.activeAnchors.includes(this.activeRoute.anchor);
      const fallbackOrange = !phase.activeReceivers.includes(this.activeRoute.receiver);
      if (fallbackBlue || fallbackOrange) {
        this.activeRoute = { anchor: fallbackBlue ? "floor" : this.activeRoute.anchor, receiver: fallbackOrange ? "safe" : this.activeRoute.receiver };
        this.emit("portal-fallback", { blue: fallbackBlue, orange: fallbackOrange, anchor: this.activeRoute.anchor, receiver: this.activeRoute.receiver });
      }
    }
    if (this.chainTier > 0 && this.tick >= this.chainExpiresAt) {
      this.chainTier = 0;
      this.emit("chain-expired", {});
    }
    // Overload disarms whenever every lane is clear (checked after feeds apply
    // this tick), keeping the getter honest even if feed() didn't already null it.
    if (this.overloadAt !== null && !this.anyLaneJammed()) this.overloadAt = null;
    // Jam-recovery run-ends only bite inside the shift (LOCKED-DESIGN 18.12):
    // once the shift bell rings the only ends are complete/overflow — no
    // post-shift cooking during the load drain-out. Actions apply before timers,
    // so a furnace-contact or exit-traversal on the expiry tick still saves you.
    if (this.tick < SHIFT_TICKS) {
      // (3) DWELL — the chamber cooks the player (always runs on entry). Dwell
      // wins a same-tick race with overload: the more personal failure.
      if (this.playerSide === "chamber" && this.dwellExpiresAt !== null && this.tick >= this.dwellExpiresAt) {
        this.failures.push({ tick: this.tick, cause: "cooked", detail: `dwelled past ${this.dwellExpiresAt}` });
        this.finish("cooked");
        return;
      }
      // (4) OVERLOAD — a neglected jam overloads the furnace, unless a drained
      // feed landed within FEED_GRACE_TICKS (a player mid-scoop is not
      // neglecting). Grace is a fixed fairness valve, not a difficulty dial.
      if (this.overloadAt !== null && this.anyLaneJammed() && this.tick >= this.overloadAt && this.tick - this.lastLaneFeedAt > FEED_GRACE_TICKS) {
        const jammed = LANES.filter((lane) => this.laneJamBulk[lane] > 0).map((lane) => `${lane}:${this.laneJamBulk[lane]}`).join(",");
        this.failures.push({ tick: this.tick, cause: "jam-overload", detail: jammed });
        this.finish("jam-overload");
        return;
      }
    }
    if (this.criticalExpiresAt !== null) {
      if (this.overflow <= OVERFLOW_SEVERE) {
        this.criticalExpiresAt = null;
        this.emit("critical-cleared", { overflow: this.overflow });
      } else if (this.tick >= this.criticalExpiresAt) {
        this.failures.push({ tick: this.tick, cause: "overflow", detail: `${this.overflow} bulk units` });
        this.finish("overflow");
      }
    }
  }

  private phaseSpec() {
    return this.seed.phases[Math.min(5, Math.max(0, Math.floor(this.tick / 1800)))]!;
  }

  private lowerGradeBand(): void {
    this.grade = this.grade >= 85 ? 84 : this.grade >= 65 ? 64 : this.grade >= 40 ? 39 : 0;
  }

  private finish(reason: RunEndReason): void {
    if (this.ended) return;
    this.ended = true;
    this.endReason = reason;
    this.emit("run-ended", { reason, score: this.score, grade: this.grade, missedLoads: this.metrics.loads.missed });
  }

  private emit(type: string, data: LedgerEvent["data"], loadId?: string, ticketId?: string): void {
    this.ledger.push({ seq: this.seq++, tick: this.tick, type, runId: this.runId, ...(loadId ? { loadId } : {}), ...(ticketId ? { ticketId } : {}), data });
  }

  bundle(performance: RunBundle["performance"] = [], notes?: string): RunBundle {
    // Honest unranked marker (LOCKED-DESIGN 18.11): derived from the seed
    // ruleset so replay reconstructs it for free, and OMITTED on standard runs
    // so the three official ranked bundles stay byte-identical.
    const mode = this.seed.ruleset === "simple" ? ("simple" as const) : undefined;
    return {
      schema: 1, gameVersion: GAME_VERSION, createdAt: new Date().toISOString(), runId: this.runId,
      seed: structuredClone(this.seed), practicePhase: this.practiceStartTick / 1800 + 1,
      trace: structuredClone(this.trace), ledger: structuredClone(this.ledger), loads: structuredClone(this.loads),
      metrics: structuredClone(this.metrics), performance: structuredClone(performance), failures: structuredClone(this.failures),
      ...(notes ? { notes } : {}),
      ...(mode ? { mode } : {}),
      // Replay-critical (LOCKED-DESIGN 18.14): both replayBundle and the
      // authoritative validator thread this back into the Simulation, so a
      // toggled Simple run replays byte-identically instead of self-quarantining.
      ...(this.jamsDisabled ? { jamsDisabled: true as const } : {}),
      final: { score: this.score, grade: this.grade, chain: this.chainTier + 1, missedLoads: this.metrics.loads.missed, tick: this.tick, reason: this.endReason, ledgerHash: hashLedger(this.ledger) },
    };
  }
}

export function replayBundle(bundle: RunBundle): RunBundle {
  const simulation = new Simulation(bundle.seed, { runId: bundle.runId, practicePhase: bundle.practicePhase, recordTrace: true, jamsDisabled: bundle.jamsDisabled });
  for (const frame of bundle.trace) simulation.step(frame);
  return simulation.bundle(bundle.performance, bundle.notes);
}

export function hashLedger(ledger: readonly LedgerEvent[]): string {
  const text = JSON.stringify(ledger);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/** murmur3 finalizer: full avalanche on a low-entropy input word (F11, m1.4.0). */
function mix32(word: number): number {
  let x = word | 0;
  x = Math.imul(x ^ (x >>> 16), 0x21f0aaad);
  x = Math.imul(x ^ (x >>> 15), 0x735a2d97);
  x ^= x >>> 15;
  return x >>> 0;
}

/**
 * Deterministic delivery roll (LOCKED-DESIGN 18.14 / playtest F11). The old
 * body ran ONE xorshift32 round over `seed ^ tick ^ len*phi` — consecutive
 * ticks differ by one bit and a single round does not avalanche low bits into
 * the high bits that decide the roll, so the output was a low-frequency square
 * wave in `tick`: 102-180 second blocks of guaranteed success, `Missed`
 * unreachable on safe, only ~661 distinct per-mille values across a whole
 * shift, and on the ranked `route-receiver` seed safe/technical could never
 * fail at all. The fix is a proper input mix plus a murmur3 finalizer; the
 * LOCKED 0.93/0.78/0.66 success table and the +0.2 partial band are unchanged
 * (this fixes the PRNG, not the tuning).
 *
 * `route` is deliberately kept OUT of the mix: both lanes share the tick's
 * roll and only the threshold differs, exactly as before.
 *
 * HONESTY RIDER (F11 defect 2, still open as a TUNING question): `Missed` is
 * STILL unreachable on safe (0.00%) because 0.93 + 0.20 >= 1.0 by construction
 * of the locked table, and a single-piece load can never be partial
 * (`Math.max(1, len - 1) === 1 === len`), so 1-piece safe loads are always
 * clean. Narrowing the partial band needs an owner decision and is out of
 * scope here.
 *
 * Exported so the distribution tests histogram the REAL kernel code rather
 * than a copy that can silently drift from it.
 */
export function deterministicDelivered(pieces: TrashKind[], route: Route, tick: number, seed: number, valid: boolean): number {
  if (!valid) return 0;
  const roll = mix32((seed | 0) ^ Math.imul(tick, 0x9e3779b9) ^ Math.imul(pieces.length, 0x85ebca6b)) / 4294967296;
  const success = route.receiver === "safe" ? 0.93 : route.receiver === "technical" ? 0.78 : 0.66;
  if (roll < success) return pieces.length;
  if (roll < success + 0.2) return Math.max(1, pieces.length - 1);
  return 0;
}

/** Recipe → HUD entries (required vs accumulated), stable recipe key order. */
function recipeEntries(ticket: TicketSpec, counts: Partial<Record<TrashKind, number>>): TicketRecipeEntry[] {
  return Object.entries(ticket.recipe).map(([kind, required]) => ({
    kind: kind as Exclude<TrashKind, "husk">,
    required: required ?? 0,
    have: counts[kind as TrashKind] ?? 0,
  }));
}

function quantize(value: number): number { return Math.round(clamp(value, -1, 1) * 127) / 127; }
function clamp(value: number, min: number, max: number): number { return Math.max(min, Math.min(max, value)); }
function makeRunId(seedId: string): string { return `${seedId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`; }
